import { Button, Checkbox, Col, DatePicker, Form, Row, Select, Space } from 'antd';
import dayjs from 'mixins/dayjsConfig.js';
import ru from 'dayjs/locale/ru';
import React, { useEffect, useRef, useState } from 'react';
import { capitalizeFirstLetter, localize } from "mixins/helpers";
import {
    TypeEnum,
    blocks,
    externalPeriodData,
    externalTypeData,
    hsCodes,
    periodData,
    typeData, exportPeriodData,
} from './search.data';
import { IDGDitem, SearchExternalProps, StateExternalViewModel } from './search.types';
import { Dayjs } from 'dayjs';
import Recaptcha from '/react4xp/mixins/ui/recaptcha/recaptcha';
import {regions} from "/react4xp/parts/stats-external-client/components/area-codes";

export function SearchExternal<T extends StateExternalViewModel>(
    {
        onFinish,
        translations,
        lang
    }: SearchExternalProps<T>
) {
    dayjs.locale(ru);

    const [regionData, setRegionData] = useState<IDGDitem[]>(regions);
    const [monthList, setMonthList] = useState<Record<string, any>[]>();
    const [form] = Form.useForm();
    const captchaRef = useRef(null);

    useEffect(() => {
        setMonthList(dayjs.months().map((x, i) => ({
            label: capitalizeFirstLetter(x),
            value: i + 1,
        })));

        // loadRegionData().then((response) => {
        //     const dgd_items = [
        //         ...response.data,
        //     ].map(x => (
        //         {
        //             ...x,
        //             order: regionOrder.indexOf(x.code)
        //         }
        //     )).sort((a, b) => a.order - b.order);
        //
        //     setRegionData(dgd_items);
        // });
    }, []);

    const disabledDate = (current: Dayjs) => {
        return current.year() < 1900 || (current && current > dayjs().endOf('year'))
    }

    const clearForm = () => {
        form.resetFields();
        setTimeout(() => onFinish(null));
    };

    const handleCheckboxOnChange = (value: any) => {
        form.setFieldValue('growing', value.target.checked);
    };

    return (
        <Form
            form={form}
            onFinish={(values) => {
                const recaptcha = captchaRef.current.getValue();

                if (recaptcha.verified) {
                    onFinish(values);
                } else {
                    captchaRef.current.setError();
                }
            }}
            layout="vertical"
        >
            <Row
                gutter={{ xs: 8, md: 10, lg: 10 }}
                justify="space-between"
            >
                <Col span={24}>
                    <Row gutter={{ xs: 8, md: 12 }}>
                        <Col span={6}>
                            <Form.Item
                                name="region"
                                label={localize('region',translations)}
                            >
                                <Select
                                    allowClear
                                    size="large"
                                    placeholder="Все"
                                    options={regionData.map((region) => ({
                                        label: region[lang],
                                        value: region.code,
                                    }))}
                                />
                            </Form.Item>
                        </Col>
                        <Form.Item
                            noStyle
                            shouldUpdate={(prev, current) => prev.region !== current.region}
                        >
                            {(instance) => {
                                const region = instance.getFieldValue('region');
                                const isRk =
                                    typeof region === 'string' || typeof region === 'number'
                                        ? String(region).includes('01')
                                        : '';

                                if (region)
                                    return (
                                        <Col span={8}>
                                            <Form.Item
                                                name="type"
                                                label={localize('type', translations)}
                                                rules={[{ required: form.getFieldValue('region') }]}
                                            >
                                                <Select
                                                    allowClear
                                                    size="large"
                                                    options={
                                                        region
                                                            ? isRk
                                                                ? typeData(translations)
                                                                : externalTypeData(translations)
                                                            : []
                                                    }
                                                />
                                            </Form.Item>
                                        </Col>
                                    );
                            }}
                        </Form.Item>
                        <Form.Item
                            noStyle
                            shouldUpdate={(prev, current) => prev.type !== current.type}
                        >
                            {(instance) => {
                                const type = instance.getFieldValue('type');
                                const isExport = [
                                    TypeEnum.TS.toString(),
                                    TypeEnum.ST.toString(),
                                ].includes(type);

                                if (type && isExport)
                                    return (
                                        <Col span={8}>
                                            <Form.Item
                                                name="tnvedCode"
                                                label="Знаки ТН ВЭД"
                                            >
                                                <Select
                                                    allowClear
                                                    size="large"
                                                    options={hsCodes(translations)}
                                                />
                                            </Form.Item>
                                        </Col>
                                    );
                            }}
                        </Form.Item>
                        <Col span={6}>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prev, current) =>
                                    prev.region !== current.region || prev.type !== current.type}
                            >
                                {(instance) => {
                                    const region: string = instance.getFieldValue('region');
                                    const type: string = instance.getFieldValue('type');
                                    const isRk = region && region.includes('01');
                                    const isExpress = [
                                        TypeEnum.EXPRESS_EXP.toString(),
                                        TypeEnum.EXPRESS_IMP.toString()
                                    ].includes(type);

                                    const isExport = type === TypeEnum.TS.toString();

                                    return (
                                        <Form.Item
                                            name="period"
                                            label={localize('period',translations)}
                                            rules={[{ required: form.getFieldValue('region') }]}
                                        >
                                            <Select
                                                allowClear
                                                size="large"
                                                options={
                                                    !isRk
                                                        ? isExport
                                                            ? exportPeriodData(translations)
                                                            : periodData(translations)
                                                        : isExpress
                                                            ? externalPeriodData(translations)
                                                            : periodData(translations)
                                                }
                                            />
                                        </Form.Item>
                                    );
                                }}
                            </Form.Item>
                        </Col>
                        <Form.Item
                            noStyle
                            shouldUpdate={(prev, current) => prev.period !== current.period}
                        >
                            {(instance) => {
                                const period = instance.getFieldValue('period') || 0;

                                if (typeof period !== "undefined") {
                                    return (
                                        <>
                                            {period === 0 && (
                                                <Col span={6}>
                                                    <Form.Item
                                                        name="month"
                                                        label={localize('month',translations)}
                                                    >
                                                        <Select
                                                            allowClear
                                                            size="large"
                                                            options={monthList}
                                                            placeholder={localize('all',translations)}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            )}
                                            {period === 1 && (
                                                <Col span={6}>
                                                    <Form.Item
                                                        name="quarter"
                                                        label={localize('quarter',translations)}
                                                    >
                                                        <Select
                                                            allowClear
                                                            size="large"
                                                            options={blocks(translations)}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            )}
                                            <Col span={6}>
                                                <Form.Item
                                                    name="year"
                                                    label={localize('year',translations)}
                                                >
                                                    <DatePicker
                                                        allowClear
                                                        picker="year"
                                                        size="large"
                                                        className="w-100"
                                                        placeholder={localize('all',translations)}
                                                        disabledDate={disabledDate}
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </>
                                    );
                                }
                            }}
                        </Form.Item>
                    </Row>
                    <Form.Item
                        shouldUpdate={(prev, current) => prev.period !== current.period}
                        noStyle
                    >
                        {(instance) => {
                            const period = instance.getFieldValue('period') || 0;

                            if (period === 0)
                                return (
                                    <Row>
                                        <Col flex="auto">
                                            <Form.Item name="growing">
                                                <Checkbox onChange={handleCheckboxOnChange}>
                                                    {localize('stats-external-cumulative-total',translations)}
                                                </Checkbox>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                );
                        }}
                    </Form.Item>

                    <Recaptcha ref={captchaRef} lang={lang} />
                    <br />

                    <Row>
                        <Col>
                            <Space>
                                <Form.Item>
                                    <Button
                                        type="default"
                                        onClick={clearForm}
                                    >
                                        {localize('button.clear',translations)}
                                    </Button>
                                </Form.Item>
                                <Form.Item>
                                    <Button
                                        htmlType="submit"
                                        type="primary"
                                    >
                                        {localize('apply',translations)}
                                    </Button>
                                </Form.Item>
                            </Space>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </Form>
    );
}
